import { useState, useCallback, useEffect } from 'react';
import { getPriceTracking, startPriceTracking, removePriceTracking, getPriceHistory } from '../services/storage';
import { PriceTracking, PriceHistory, ProductListing } from '../types';

export function usePriceTracking() {
  const [trackingList, setTrackingList] = useState<PriceTracking[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [priceHistory, setPriceHistory] = useState<Record<string, PriceHistory[]>>({});

  const fetchTracking = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getPriceTracking();
      setTrackingList(data);

      const histories: Record<string, PriceHistory[]> = {};
      for (const tracking of data) {
        histories[tracking.id] = await getPriceHistory(tracking.id);
      }
      setPriceHistory(histories);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch price tracking');
    } finally {
      setLoading(false);
    }
  }, []);

  const addTracking = useCallback(
    async (product: ProductListing, targetPrice?: number) => {
      try {
        await startPriceTracking(product, targetPrice);
        await fetchTracking();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to add price tracking');
      }
    },
    [fetchTracking]
  );

  const removeTracking = useCallback(
    async (trackingId: string) => {
      try {
        await removePriceTracking(trackingId);
        setTrackingList((prev) => prev.filter((t) => t.id !== trackingId));
        setPriceHistory((prev) => {
          const updated = { ...prev };
          delete updated[trackingId];
          return updated;
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to remove price tracking');
      }
    },
    []
  );

  const getTrackedProduct = useCallback((productId: string): PriceTracking | undefined => {
    return trackingList.find((t) => t.product_id === productId);
  }, [trackingList]);

  useEffect(() => {
    fetchTracking();
  }, [fetchTracking]);

  return {
    trackingList,
    loading,
    error,
    addTracking,
    removeTracking,
    priceHistory,
    getTrackedProduct,
    refresh: fetchTracking,
  };
}